// strona listy produktów
// @ts-nocheck

import React, { FC, useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import qs from 'query-string';
import { Grid } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { ChevronLeft, Funnel } from 'react-bootstrap-icons';
import { Skeleton } from '@mui/material';

import { reduxActions, useDispatch, useSelector } from 'store';
import {
  useGetProducts,
  useGetProductsMutation,
  useGetProductsFiltersAdditional,
  useGetProductsFiltersMain,
  useGetProductsSortMethods,
  useGetProductsTitle,
  useGetProductsBreadcrumbs
} from 'api';
import { useGetCategoryDescription } from 'api/endpoints/useGetCategoryDescription';
import { IProductsRequest, IProductsSortMethod } from 'api/types';
import { useAppNavigate, useRWD, useScrollDown, usePrevious } from 'hooks';
import {
  FiltersSidebar,
  FiltersTopBar,
  FiltersOverlay,
  Categories
} from 'pages/Products/components';
import { MobileProductItem, ProductItem, Search } from 'components/containers';
import ProductCard from 'components/containers/ProductCard/ProductCard';
import { Breadcrumbs, Container, Loader, Pagination, Select } from 'components/controls';
import ActiveFilters from 'pages/Products/components/ActiveFilters/ActiveFilters';
import MobileFiltersOverlay from 'pages/Products/components/MobileFiltersOverlay/MobileFiltersOverlay';
import GridSwitcher, { IProps as IGridSwitcherProps } from 'components/controls/GridSwitcher';
import { generateCategoryPageScheme } from 'api/helpers';

import styles from 'theme/pages/Products/Products.module.scss';
import logo from 'assets/logo.svg';

/**
 * Domyślne sortowanie zależne od kontekstu (INHB-876):
 *  - wyszukiwanie (jest fraza) → DEFAULT = trafność po nazwie (ranking z core),
 *  - kategoria/tryb → POPULARITY_SCORE_DESC = po popularności/sprzedaży (INHB-185).
 * Wcześniej front zawsze wymuszał popularność, co psuło ranking trafności w wyszukiwarce.
 */
const SEARCH_DEFAULT_SORT = 'DEFAULT';
const CATEGORY_DEFAULT_SORT = 'POPULARITY_SCORE_DESC';

const isSearchContext = (searchKeyword?: string | null) => !!searchKeyword;

const getDefaultSort = (searchKeyword?: string | null) =>
  isSearchContext(searchKeyword) ? SEARCH_DEFAULT_SORT : CATEGORY_DEFAULT_SORT;

/**
 * Ustala metodę sortowania: jawny wybór (z URL lub od użytkownika) ma priorytet,
 * w przeciwnym razie domyślna metoda właściwa dla kontekstu.
 */
const resolveSortMethod = (raw: string | undefined, searchKeyword?: string | null) =>
  raw || getDefaultSort(searchKeyword);

interface IFilter {
  filter_id: string;
  filter_value: string;
}

interface IProps {
  categoryId?: string;
  mode?: 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS';
  h1?: string | null;
  hasSeoTitle?: boolean;
}

const Products: FC<IProps> = ({ categoryId, mode, h1, hasSeoTitle }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();

  const { category_id } = qs.parse(location.search);

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = location.pathname.slice(0, getPosition(location.pathname, '/', 2));

  const { isMobile, isTablet } = useRWD();
  const { isScrollDown } = useScrollDown();

  // kategoria pochodząca z url resolvera
  const urlResolverCategoryId = parseInt(categoryId || '0');

  const categoryIdQueryParam = urlResolverCategoryId
    ? { category_id: urlResolverCategoryId }
    : typeof category_id === 'string'
      ? { category_id: parseInt(category_id) }
      : {};

  // poprzednia wartość kategorii
  const prevCategoryId = usePrevious(categoryIdQueryParam.category_id);

  // globalnie ustawiona kategoria i fraza wyszukiwarki
  const { searchKeyword: globalSearchKeyword } = useSelector((state) => state.products);

  // poprzednia wartość kategorii
  const prevGlobalSearchKeyword = usePrevious(globalSearchKeyword);

  // Aktualne filtry
  const [queryFilters, setQueryFilters] = useState<IFilter[]>([]);
  const [isFilterMobile, setIsFilterMobile] = useState(false);
  const [trigerReset, setTriggerReset] = useState(false);
  
  // State do przełączania między Kategorie a Filtry (desktop)
  const [activeTab, setActiveTab] = useState<'categories' | 'filters'>('categories');
  // State do zarządzania overlayem na mobile
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
  const [mobileOverlayTab, setMobileOverlayTab] = useState<'categories' | 'filters' | 'sorting'>('categories');

  const localStorageGridType = localStorage.getItem('gridType') as 'grid' | 'line' | null;

  // Widok listy (boxy/linie)
  const [gridType, setGridType] = useState<IGridSwitcherProps['type']>(
    localStorageGridType || 'grid'
  );

  //Używamy ref aby śledzić czy to pierwsze renderowanie
  const isFirstRender = useRef(true);

  const urlQuery = qs.parseUrl(window.location.href, { parseNumbers: false }).query;
  
  const [productsQuery, setProductsQuery] = useState<IProductsRequest>({
    page: 1,
    limit: 20,
    search_keyword: '',
    filter_attributes: '',
    sort_method: resolveSortMethod(
      urlQuery.sortMethod as string,
      urlQuery.search_keyword as string
    ),
    mode: mode,
    ...categoryIdQueryParam,
    ...urlQuery
  });

  const { data: productsBreadcrumbsData, isFetching: isBredcrumbsLoading } =
    useGetProductsBreadcrumbs(productsQuery);

  const { data: mainFiltersData, isLoading: isMainFiltersLoading } = useGetProductsFiltersMain({
    ...productsQuery,
    page: 1,
    limit: 999
  });

  const { data: additionalFiltersData, isLoading: isAdditionalFiltersLoading } =
    useGetProductsFiltersAdditional({
      ...productsQuery,
      page: 1,
      limit: 999
    });

  const { data: productsSimpleData, isLoading: isLoadingProducts } = useGetProducts(
    {
      scopes:
        'BasicProductData|ImageFormattedProductData|UnitProductData|StockFormattedProductData',
      ...productsQuery
    },
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        refetchProductsFullData({
          scopes: 'PriceProductData',
          productIds: data.items
            .map((item) => item.id)
            .toString()
            .replaceAll(',', '|'),
          ...productsQuery
        });
      }
    }
  );

  // Pobranie listy produktów
  const { data: productsFullData, mutate: refetchProductsFullData } = useGetProductsMutation();

  const productsData = useMemo(
    () => ({
      ...productsSimpleData,
      ...productsFullData
    }),
    [productsSimpleData, productsFullData]
  );

  // Pobranie nagłówka
  const { data: productsTitleData } = useGetProductsTitle(productsQuery);

  // Pobranie opcji sortowania
  const { data: productsSortingMethodsData } = useGetProductsSortMethods({
    page: 1,
    limit: 999
  });

  const validSortIds = useMemo(
    () => new Set((productsSortingMethodsData?.items ?? []).map((item) => item.id)),
    [productsSortingMethodsData?.items]
  );

  const categoryIdStr = categoryIdQueryParam.category_id?.toString() || '';
  const { data: seoDescriptionData } = useGetCategoryDescription(categoryIdStr, {
    enabled: !!categoryIdStr
  });

  const urlSearchKeyword = qs.parseUrl(location.search).query.search_keyword;

  useEffect(() => {
    typeof urlSearchKeyword === 'string' &&
    dispatch(reduxActions.setSearchKeyword(urlSearchKeyword));
  }, [urlSearchKeyword]);

  // ustawienie globalnej kategorii
  useEffect(() => {
    if (categoryIdQueryParam.category_id !== prevCategoryId && categoryIdQueryParam.category_id) {
      dispatch(reduxActions.setCategoryId(categoryIdQueryParam.category_id));
    }
  }, [categoryIdQueryParam]);

  // usuwanie globalnej kategorii
  useEffect(() => {
    return () => {
      dispatch(reduxActions.setCategoryId(undefined));
      dispatch(reduxActions.setSearchFilterCategory({ id: null, name: '', url_link: null }));
    };
  }, []);

  // Zmiana url'a przy zmianie parametrów zapytania do API
  useEffect(() => {
    const { limit, category_id, ...restQuery } = productsQuery;

    const navigationUrl = categoryId
      ? `${location.pathname.replace(urlPrefix, '')}?${qs.stringify(restQuery, {
        skipEmptyString: true
      })}`
      : `${location.pathname.replace(urlPrefix, '')}?${qs.stringify(
        { ...restQuery, category_id },
        {
          skipEmptyString: true
        }
      )}`;

    navigate(navigationUrl, { replace: true });
  }, [
    productsQuery.page,
    productsQuery.filter_attributes,
    productsQuery.sort_method,
    productsQuery.search_keyword
  ]);

  // Ustawienie aktywnych filtrów z url'a (podczas wejścia na stronę)
  useEffect(() => {
    if (isFirstRender.current) {
      typeof productsQuery.search_keyword === 'string' &&
      dispatch(reduxActions.setSearchKeyword(productsQuery.search_keyword));
      setQueryFilters(
        productsQuery.filter_attributes
          ?.split('|')
          .filter((item) => item)
          .map((queryFilter) => {
            const queryFilterArray = queryFilter.split('=');
            return {
              filter_id: queryFilterArray[0],
              filter_value: queryFilterArray[1]
            };
          }) || []
      );
      isFirstRender.current = false;
    }
  }, []);

  // Ustawienie breadcrumbs'ów (przy renderowaniu strony)
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs(
        productsBreadcrumbsData
          ? productsBreadcrumbsData.items.map((item) => ({
            name: item.name,
            path: item.category_id ? `/${item.url}` : undefined
          }))
          : []
      )
    );
  }, [productsBreadcrumbsData?.items]);

  // Gdy sort_method nie ma na liście z API → ustaw domyślne dla kontekstu (zawsze coś wybrane)
  useEffect(() => {
    if (validSortIds.size === 0) return;
    const current = productsQuery.sort_method;
    if (!current || !validSortIds.has(current)) {
      setProductsQuery((prev) => ({
        ...prev,
        sort_method: getDefaultSort(prev.search_keyword),
        page: 1
      }));
    }
  }, [validSortIds, productsQuery.sort_method, productsQuery.search_keyword]);

  // aktualizacja parametrów zapytania przy zmianie kategorii
  useEffect(() => {
    if (categoryIdQueryParam.category_id !== prevCategoryId) {
      const currentUrlParams = qs.parseUrl(window.location.href, { parseNumbers: false }).query;
      const currentModeFromUrl = currentUrlParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;

      setProductsQuery((prevState) => ({
        ...prevState,
        mode: currentModeFromUrl || undefined,
        filter_attributes: '',
        page: 1,
        category_id: categoryIdQueryParam.category_id
      }));

      // Resetujemy również filtry w UI
      setQueryFilters([]);
    }
  }, [categoryIdQueryParam.category_id, prevCategoryId, categoryId, location.search]);

  useEffect(() => {
    if (globalSearchKeyword !== prevGlobalSearchKeyword && prevGlobalSearchKeyword !== undefined) {
      setProductsQuery((prevState) => ({
        ...prevState,
        mode: undefined,
        filter_attributes: '',
        search_keyword: globalSearchKeyword,
        // INHB-876: nowe wyszukiwanie wraca do sortowania po trafności
        sort_method: getDefaultSort(globalSearchKeyword),
        page: 1
      }));

      // Resetujemy filtry
      setQueryFilters([]);
    }
  }, [globalSearchKeyword, prevGlobalSearchKeyword]);

  // Uaktualnienie filtrów w zapytaniu do API
  useEffect(() => {
    setProductsQuery((prevState) => ({
      ...prevState,
      filter_attributes: queryFilters
        .map((filter) => `${filter.filter_id}=${filter.filter_value}`)
        .join('|')
    }));
  }, [queryFilters]);

  // Uaktualnienie kategorii (w globalnym stanie) po zmianie tej danej w url'u
  useEffect(() => {
    const currentUrlParams = qs.parseUrl(window.location.href, { parseNumbers: false }).query;
    const currentModeFromUrl = currentUrlParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;
    const activeMode = mode || currentModeFromUrl;

    if (activeMode) {
      setProductsQuery((prevState) => ({
        ...prevState,
        limit: 30,
        mode: activeMode
      }));
    }
  }, [mode]);

  // Funkcja aktualizująa filtry (w stanie komponentu i zapytaniu do API)
  const updateQueryFilters = (filters: IFilter[]) => {
    setQueryFilters(filters);
    setProductsQuery((prevState) => ({
      ...prevState,
      filter_attributes: filters
        .map((filter) => `${filter.filter_id}=${filter.filter_value}`)
        .join('|'),
      page: 1
    }));
  };

  // Generowanie JSON-LD schema dla kategorii
  const categoryPageScheme = useMemo(() => {
    if (!productsSimpleData || !productsTitleData) return null;
    return generateCategoryPageScheme(
      productsSimpleData,
      productsTitleData.name,
      window.location.href
    );
  }, [productsSimpleData, productsTitleData]);

  const renderProducts = () =>
    productsSimpleData?.items?.map((simpleProduct) => {
      const product = productsFullData
        ? {
          ...simpleProduct,
          ...productsFullData?.items.find((fullProduct) => fullProduct.id === simpleProduct.id)
        }
        : simpleProduct;

      return (
        <Grid
          key={product.id}
          item
          md={gridType === 'line' ? 12 : isTablet ? 4 : 3}
          itemProp="itemListElement"
          itemScope
          itemType="http://schema.org/ListItem">
          <ProductCard
            product={product}
            categoryId={categoryIdQueryParam.category_id}
            searchKeywords={globalSearchKeyword}
            line={gridType === 'line'}
          />
        </Grid>
      );
    });

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Products')}>
      {productsTitleData && (
        <Helmet>
          {!hasSeoTitle && <title>{productsTitleData.name}</title>}
          {/* INHB-929: canonical bez parametrów zapytania */}
          <link rel="canonical" href={`${window.location.origin}${window.location.pathname}`} />
          {categoryPageScheme &&
            categoryPageScheme.map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))}
        </Helmet>
      )}
      
      {!isMobile && <Breadcrumbs isLoading={isBredcrumbsLoading} />}

      {/* Overlay tylko gdy isFilterMobile jest true (stary sposób) */}
      {isFilterMobile && isMobile && (
        <MobileFiltersOverlay
          categoryId={categoryIdQueryParam.category_id}
          onChange={updateQueryFilters}
          queryFilters={queryFilters}
          filtersData={[
            ...(mainFiltersData?.items || []),
            ...(additionalFiltersData?.items || [])
          ]}
          close={() => setIsFilterMobile(false)}
          productsSortingMethodsData={productsSortingMethodsData}
          productsQuery={productsQuery}
          setSort={(id: string) =>
            setProductsQuery({
              ...productsQuery,
              sort_method: resolveSortMethod(id),
              page: 1
            })
          }
          searchKeyword={globalSearchKeyword}
          productsCount={productsSimpleData?.total_count}
        />
      )}

      {!isFilterMobile && (
        <React.Fragment>
          {/* Mobile header z logo i search - poza Container */}
          {isMobile && (
            <div className={styles.mobileHeader}>
              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    dispatch(reduxActions.setSearchKeyword(''));
                    setTriggerReset(true);
                  }, 10);
                }}
                className={styles.mobileLogo}>
                <img src={logo} alt="logo" />
              </button>
              <div className={styles.mobileSearchWrapper}>
                <Search trigerReset={trigerReset} setTriggerReset={setTriggerReset} />
              </div>
            </div>
          )}

          {/* Mobile tabs - poza Container */}
          {isMobile && (
            <div className={styles.mobileTabs}>
              <button
                className={classnames(styles.mobileTab, {
                  [styles.active]: mobileOverlayOpen && mobileOverlayTab === 'categories'
                })}
                onClick={() => {
                  setMobileOverlayTab('categories');
                  setMobileOverlayOpen(true);
                }}>
                {t('Kategorie')}
              </button>
              <button
                className={classnames(styles.mobileTab, {
                  [styles.active]: mobileOverlayOpen && mobileOverlayTab === 'filters'
                })}
                onClick={() => {
                  setMobileOverlayTab('filters');
                  setMobileOverlayOpen(true);
                }}>
                {t('Filtry')}
              </button>
              <button
                className={classnames(styles.mobileTab, {
                  [styles.active]: mobileOverlayOpen && mobileOverlayTab === 'sorting'
                })}
                onClick={() => {
                  setMobileOverlayTab('sorting');
                  setMobileOverlayOpen(true);
                }}>
                {t('Sortowanie')}
              </button>
            </div>
          )}

          {/* Mobile breadcrumbs i nagłówek - pod zakładkami */}
          {isMobile && (
            <>
              <div className={styles.mobileBreadcrumbsWrapper}>
                <Breadcrumbs isLoading={isBredcrumbsLoading} />
              </div>
              <div className={styles.mobileTitleSection}>
                <div className={styles.title}>
                  {productsTitleData ? (
                    <>
                      <h1>{productsTitleData.name}</h1>{' '}
                      <span>({productsTitleData.products_count})</span>
                    </>
                  ) : (
                    <Skeleton width={200} height={50} />
                  )}
                </div>
              </div>
              {/* Aktywne filtry na mobile - pod nagłówkiem */}
              {queryFilters.length > 0 && (
                <div className={styles.mobileActiveFiltersWrapper}>
                  <ActiveFilters
                  queryFilters={queryFilters}
                  filtersData={[
                    ...(mainFiltersData?.items || []),
                    ...(additionalFiltersData?.items || [])
                  ]}
                  onRemoveFilter={(filterId, filterValue) => {
                    const newFilters = queryFilters.filter(
                      (f) =>
                        !(
                          f.filter_id === filterId &&
                          (!filterValue || f.filter_value === filterValue)
                        )
                    );
                    updateQueryFilters(newFilters);
                  }}
                  onClearAll={() => updateQueryFilters([])}
                />
                </div>
              )}
            </>
          )}

          {/* Mobile overlay */}
          {isMobile && mobileOverlayOpen && (
            <MobileFiltersOverlay
              categoryId={categoryIdQueryParam.category_id}
              onChange={updateQueryFilters}
              queryFilters={queryFilters}
              filtersData={[
                ...(mainFiltersData?.items || []),
                ...(additionalFiltersData?.items || [])
              ]}
              close={() => setMobileOverlayOpen(false)}
              productsSortingMethodsData={productsSortingMethodsData}
              productsQuery={productsQuery}
              setSort={(id: string) => {
                setProductsQuery({
                  ...productsQuery,
                  sort_method: resolveSortMethod(id),
                  page: 1
                });
              }}
              searchKeyword={globalSearchKeyword}
              productsCount={productsSimpleData?.total_count}
              initialTab={mobileOverlayTab}
            />
          )}

          <Container>
            {/* Nagłówek na górze - poza content (desktop) */}
            <div className={classnames(styles.bottomBar, { [styles.isScrollDown]: !isScrollDown })}>
              <div className={styles.bottomBarLeft}>
                <div className={styles.title}>
                  {productsTitleData ? (
                    <>
                      <Link className={styles.arrow} to="/">
                        <ChevronLeft />
                      </Link>{' '}
                      <h1>{h1 || productsTitleData.name}</h1>{' '}
                      <span>({productsTitleData.products_count})</span>
                    </>
                  ) : (
                    <Skeleton width={200} height={50} />
                  )}
                </div>

                {seoDescriptionData?.article_fields?.[0]?.value && (
                  <h2
                    className={styles.seoBlock}
                    dangerouslySetInnerHTML={{
                      __html: seoDescriptionData?.article_fields?.[0].value
                    }}
                  />
                )}

                <div className={styles.filterWrapper}>
                  <div className={styles.filterInfo}>
                    <span>
                      <Trans>Wyniki wyszukiwania</Trans>
                    </span>
                    <span>({productsSimpleData?.total_count})</span>
                  </div>

                  <div onClick={() => setIsFilterMobile(true)} className={styles.filterButton}>
                    <Funnel />{' '}
                    <div>
                      <Trans>Filtruj</Trans>/<Trans>Sortuj</Trans>
                    </div>
                    <div className={styles.filterBox}>0</div>
                  </div>
                </div>
              </div>

              {/* Sortowanie i widok po prawej stronie */}
              {!isMobile && (
                <div className={styles.bottomBarRight}>
                  <div className={styles.sortingSelectWrapper}>
                    <Select<IProductsSortMethod>
                      onChange={(sortMethod) =>
                        sortMethod &&
                        setProductsQuery({
                          ...productsQuery,
                          sort_method: resolveSortMethod(sortMethod.id),
                          page: 1
                        })
                      }
                      value={productsQuery.sort_method}
                      options={
                        productsSortingMethodsData?.items.map((item) => ({
                          value: item.id,
                          label: item.name,
                          item
                        })) || []
                      }
                      placeholder={t('Sortowanie')}
                    />
                  </div>

                  <GridSwitcher
                    type={gridType}
                    onChange={(type) => {
                      localStorage.setItem('gridType', type);
                      setGridType(type);
                    }}
                  />
                </div>
              )}
            </div>

            <div className={styles.content}>
            {!isMobile && (
              <div className={styles.categories}>
                {/* Przełączanie między Kategorie a Filtry */}
                <div className={styles.sidebarTabs}>
                  <button
                    className={classnames(styles.sidebarTab, {
                      [styles.active]: activeTab === 'categories'
                    })}
                    onClick={() => setActiveTab('categories')}>
                    {t('Kategorie')}
                  </button>
                  <button
                    className={classnames(styles.sidebarTab, {
                      [styles.active]: activeTab === 'filters'
                    })}
                    onClick={() => setActiveTab('filters')}>
                    {t('Filtry')}
                  </button>
                </div>

                {/* Warunkowe renderowanie komponentów */}
                {activeTab === 'categories' ? (
                  <Categories
                    searchKeyword={globalSearchKeyword}
                    chosenCategoryId={categoryIdQueryParam.category_id}
                    productsQueryParams={productsQuery}
                  />
                ) : (
                  <>
                    {(isAdditionalFiltersLoading || isMainFiltersLoading) && <Loader />}
                    <FiltersSidebar
                      categoryId={categoryIdQueryParam.category_id}
                      onChange={updateQueryFilters}
                      queryFilters={queryFilters}
                      filtersData={[
                        ...(mainFiltersData?.items || []),
                        ...(additionalFiltersData?.items || [])
                      ]}
                      productsCount={productsSimpleData?.total_count}
                    />
                  </>
                )}
              </div>
            )}

            <div className={styles.list}>
              {/* Search tylko na desktop */}
              {!isMobile && (
                <div className={styles.searchWrapper}>
                  <Search />
                </div>
              )}

              {/* Aktywne filtry nad listingiem - tylko na desktop (na mobile są w mobileActiveFiltersWrapper) */}
              {!isMobile && (
                <ActiveFilters
                  queryFilters={queryFilters}
                  filtersData={[
                    ...(mainFiltersData?.items || []),
                    ...(additionalFiltersData?.items || [])
                  ]}
                  onRemoveFilter={(filterId, filterValue) => {
                    const newFilters = queryFilters.filter(
                      (f) =>
                        !(
                          f.filter_id === filterId &&
                          (!filterValue || f.filter_value === filterValue)
                        )
                    );
                    updateQueryFilters(newFilters);
                  }}
                  onClearAll={() => updateQueryFilters([])}
                />
              )}

              {/* Marka, dostępność i cena - zakomentowane */}
              {/* {!isMobile && (
                <Grid item sm={12}>
                  <div className={styles.actionsTopBar}>
                    {isMainFiltersLoading ? (
                      <Skeleton height={34} width={300} />
                    ) : (
                      <FiltersTopBar
                        onChange={updateQueryFilters}
                        queryFilters={queryFilters}
                        filtersData={mainFiltersData?.items}
                      />
                    )}
                  </div>
                </Grid>
              )} */}

              <Grid
                container
                columnSpacing={isMobile ? "0px" : "12px"}
                rowSpacing={isMobile ? "24px" : "12px"}
                itemScope
                itemType="http://schema.org/ItemList">
                {renderProducts()}

                {isLoadingProducts && (
                  <Grid item sm={12} className={styles.loadingWrapper}>
                    <Loader />
                  </Grid>
                )}
              </Grid>
            </div>
          </div>
          <Pagination
            pagesCount={productsSimpleData?.total_pages || 1}
            page={productsQuery.page || 1}
            onChange={(page) => setProductsQuery({ ...productsQuery, page })}
          />
          {seoDescriptionData?.article_fields?.[1]?.value && (
            <h2
              className={styles.seoBlock}
              dangerouslySetInnerHTML={{ __html: seoDescriptionData?.article_fields?.[1].value }}
            />
          )}
        </Container>
        </React.Fragment>
      )}
    </div>
  );
};

export default Products;
